import { Link } from "react-router-dom";
import PageShell from "./components/layout/PageShell";
import { Reveal, Wave } from "./components/ui/Reveal";
import { FormulaCard } from "./components/FormulaCard";
import { useFormulas } from "./hooks/useFormulas";
import { useFormConfig } from "./hooks/useFormConfig";

const STEPS = [
  {
    image: "/images/ink/step-aanvraag.jpg",
    title: "Vraag je offerte aan",
    text: "Kies een formule, je datum en het aantal gasten. Het duurt een paar minuten.",
  },
  {
    image: "/images/ink/step-offerte.jpg",
    title: "Onderteken online",
    text: "Binnen 48 uur krijg je je offerte in je mailbox. Je bekijkt en ondertekent ze met één klik.",
  },
  {
    image: "/images/ink/step-feest.jpg",
    title: "Wij bakken op je feest",
    text: "Je krijgt een bevestiging met je volledige bestelling. Op de dag zelf staat de foodtruck klaar en bakken we alles vers.",
  },
];

export default function App() {
  const { formulas, error: formulasError } = useFormulas();
  const config = useFormConfig();

  return (
    <PageShell>
      {/* Hero */}
      <section className="relative overflow-hidden bg-bl-black text-white">
        <div className="plus-pattern absolute inset-0 opacity-[0.04]" aria-hidden="true" />
        <div className="page-container relative grid items-center gap-12 py-14 md:py-20 lg:grid-cols-[1.05fr_1fr]">
          <div className="space-y-6 text-center lg:text-left">
            <p className="hero-rise eyebrow">Burgerlijk On Tour</p>
            <h1
              className="hero-rise font-display text-5xl font-black leading-[0.95] tracking-tight md:text-7xl"
              style={{ "--rise-delay": "80ms" } as React.CSSProperties}
            >
              Wij bakken
              <br />
              op <span className="text-bl-gold">jouw</span> feest.
            </h1>
            <p
              className="hero-rise mx-auto max-w-xl text-lg text-white/75 lg:mx-0"
              style={{ "--rise-delay": "160ms" } as React.CSSProperties}
            >
              Onze foodtruck brengt Burgerlijk-burgers naar je huwelijk, verjaardag, communie of bedrijfsevent. Vers gebakken, ter plaatse, voor {config.minimumPersonen} gasten of meer.
            </p>
            <div
              className="hero-rise flex flex-col justify-center gap-3 sm:flex-row lg:justify-start"
              style={{ "--rise-delay": "240ms" } as React.CSSProperties}
            >
              <Link to="/offerte" className="btn-red text-base">
                Vraag je offerte aan
              </Link>
              <Link to="/#formules" className="btn-light text-base">
                Bekijk de formules
              </Link>
            </div>
            <dl
              className="hero-rise grid grid-cols-3 gap-4 pt-6"
              style={{ "--rise-delay": "320ms" } as React.CSSProperties}
            >
              {[
                ["4de", "Best Burger BeNeLux 2022"],
                ["8+ jaar", "met de foodtruck"],
                [`${config.minimumPersonen}+`, "gasten per event"],
              ].map(([value, label]) => (
                <div key={label} className="text-center lg:text-left">
                  <dt className="sr-only">{label}</dt>
                  <dd className="font-display text-3xl font-bold text-bl-gold md:text-4xl">{value}</dd>
                  <dd className="text-sm text-white/55">{label}</dd>
                </div>
              ))}
            </dl>
          </div>

          <div className="hero-photo relative mx-auto w-full max-w-xl">
            <div className="absolute inset-0 translate-x-3 translate-y-3 -rotate-2 rounded-lg bg-bl-brown" aria-hidden="true" />
            <div className="relative overflow-hidden rounded-lg shadow-2xl">
              <img
                src="/images/food-truck-bg.jpg"
                alt="De Burgerlijk foodtruck 's avonds, met het luik open en de keuken verlicht"
                className="aspect-[4/3] w-full object-cover"
              />
              {/* The hatch light breathes, as if the grill just fired up */}
              <div
                className="hatch-glow pointer-events-none absolute left-[22%] top-[28%] h-[30%] w-[36%] rounded-full bg-bl-gold mix-blend-screen blur-3xl"
                aria-hidden="true"
              />
            </div>
          </div>
        </div>
        <Wave fill="#FFFFFF" />
      </section>

      {/* Formulas */}
      <section id="formules" className="scroll-mt-4 bg-white pb-20 pt-8 md:pb-28">
        <div className="page-container">
          <Reveal className="mx-auto max-w-2xl text-center">
            <p className="eyebrow">Onze formules</p>
            <h2 className="section-title mt-4">Kies wat er op je feest gebakken wordt</h2>
            <p className="mt-4 text-bl-black/65">
              Proef van alles met het Mini Burger Festival, of kies een vast assortiment en betaal enkel de burgers die effectief gegeten worden.
            </p>
          </Reveal>

          {formulasError && <p className="mt-10 text-center text-bl-black/60">{formulasError}</p>}

          <div className="mt-14 grid gap-6 md:grid-cols-2 xl:grid-cols-4">
            {formulas.map((formula, index) => (
              <Reveal key={formula.id} delay={index * 90} className="h-full">
                <FormulaCard
                  formula={formula}
                  footer={
                    <Link
                      to={`/offerte?formule=${encodeURIComponent(formula.slug)}`}
                      className={`${formula.highlighted ? "btn-red" : "btn-outline"} w-full px-4`}
                      aria-label={`Offerte aanvragen voor ${formula.name}`}
                    >
                      Kies deze formule
                    </Link>
                  }
                />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Story */}
      <section className="bg-bl-cream py-20 md:py-28">
        <div className="page-container grid items-center gap-12 lg:grid-cols-2">
          <Reveal className="bg-bl-cream">
            <img
              src="/images/bread.jpg"
              alt="Vers gebakken broodjes en Niels en Jana met hun Best Burger BeNeLux award"
              className="mx-auto w-full max-w-lg mix-blend-multiply"
            />
          </Reveal>
          <Reveal delay={120} className="space-y-5">
            <p className="eyebrow">Over ons</p>
            <h2 className="section-title">Premium is het minimum</h2>
            <p className="text-bl-black/75">
              Met meer dan 10 jaar ervaring in het maken van de perfecte burger weten we bij Burgerlijk precies hoe het moet, denken we toch ;-).
            </p>
            <p className="text-bl-black/75">
              En dat bleef niet onopgemerkt: in 2022 werden we <strong className="text-bl-black">4de in de Best Burger BeNeLux</strong>. Al meer dan 8 jaar brengen we die kwaliteit ook naar jou, met onze foodtruck.
            </p>
            <a href="https://www.burgerlijk.com/over-ons" className="btn-outline">
              Meer over Burgerlijk
            </a>
          </Reveal>
        </div>
      </section>

      {/* How it works */}
      <section className="relative bg-bl-black text-white">
        <Wave fill="#FFF8F3" className="rotate-180" />
        <div className="page-container py-16 md:py-20">
          <Reveal className="max-w-2xl">
            <p className="eyebrow">Zo werkt het</p>
            <h2 className="section-title mt-4">Van aanvraag tot foodtruck op je feest</h2>
          </Reveal>
          <ol className="mt-12 grid gap-8 md:grid-cols-3">
            {STEPS.map((step, index) => (
              <Reveal as="li" key={step.title} delay={index * 120} className="relative">
                <div className="overflow-hidden rounded-lg bg-bl-cream">
                  <img src={step.image} alt="" loading="lazy" className="aspect-[4/3] w-full object-cover mix-blend-multiply" />
                </div>
                <span className="mt-5 block font-display text-5xl font-black text-bl-gold/90">{index + 1}</span>
                <h3 className="mt-3 text-xl font-bold">{step.title}</h3>
                <p className="mt-2 text-white/65">{step.text}</p>
              </Reveal>
            ))}
          </ol>
          <Reveal className="mt-10">
            <Link to="/werking-info" className="text-sm font-medium text-bl-gold underline-offset-4 hover:underline">
              Praktische info: plaats, stroom en timing →
            </Link>
          </Reveal>
        </div>
        <Wave fill="#FFF8F3" />
      </section>

      {/* Closing call to action */}
      <section className="bg-bl-cream pb-20 pt-6 md:pb-28">
        <div className="page-container grid items-center gap-10 lg:grid-cols-2">
          <Reveal className="bg-bl-cream">
            <img
              src="/images/ink/scene-feest.jpg"
              alt=""
              loading="lazy"
              className="mx-auto w-full max-w-2xl rounded-lg mix-blend-multiply"
            />
          </Reveal>
          <Reveal delay={120} className="space-y-5 text-center lg:text-left">
            <h2 className="section-title">Is het luik al open voor jouw datum?</h2>
            <p className="text-bl-black/70">
              Vraag vrijblijvend je offerte aan. Binnen 48 uur heb je ze in je mailbox.
            </p>
            <Link to="/offerte" className="btn-red text-base">
              Vraag je offerte aan
            </Link>
          </Reveal>
        </div>
      </section>
    </PageShell>
  );
}
